import {
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  Injector,
  input,
  model,
  OnDestroy,
  OnInit,
  output,
  signal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormValueControl } from '@angular/forms/signals';
import { MatIconModule } from '@angular/material/icon';
import {
  injectUiFormControlBinding,
  proffeoUiFormErrorMessage,
  ProffeoUiFormErrors
} from '@app/shared/ui/forms/ui-form-control';
import { TableColumnMenuComponent } from '@app/shared/ui/tiptap-editor/extensions/table/menus/table-column/table-column-menu.component';
import { TableRowMenuComponent } from '@app/shared/ui/tiptap-editor/extensions/table/menus/table-row/table-row-menu.component';
import { EditorCustomNodeMenuComponent } from '@app/shared/ui/tiptap-editor/menus/editor-custom-node-menu/editor-custom-node-menu.component';
import { EditorNewBlockMenuComponent } from '@app/shared/ui/tiptap-editor/menus/editor-new-block-menu/editor-new-block-menu.component';
import { EditorTextMenuComponent } from '@app/shared/ui/tiptap-editor/menus/editor-text-menu/editor-text-menu.component';
import { ProffeoUiTiptapEditorVariant } from '@app/shared/ui/types/tiptap-editor.types';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Editor } from '@tiptap/core';
import { Color } from '@tiptap/extension-color';
import { Highlight } from '@tiptap/extension-highlight';
import { TaskItem, TaskList } from '@tiptap/extension-list';
import { Subscript } from '@tiptap/extension-subscript';
import { Superscript } from '@tiptap/extension-superscript';
import { TextAlign } from '@tiptap/extension-text-align';
import { TextStyle } from '@tiptap/extension-text-style';
import { Typography } from '@tiptap/extension-typography';
import { Youtube } from '@tiptap/extension-youtube';
import { Placeholder } from '@tiptap/extensions';
import StarterKit from '@tiptap/starter-kit';
import { TiptapEditorDirective } from 'ngx-tiptap';

import ResizableImageExtension from '@app/shared/ui/tiptap-editor/extensions/resizeable-image-extension';
import { Table, TableCell, TableHeader, TableRow } from '@app/shared/ui/tiptap-editor/extensions/table';

/**
 * DS rich text editor — tiptap-based multi-line control for formatted content (task descriptions, comments).
 *
 * @summary Primary API: `value` model. Supports Signal Forms `[formField]` (FormValueControl) and standalone
 * `[(value)]`, mirroring `proffeo-ui-textarea`.
 * With `[formField]` errors show once the field is touched (editor blur or `submit()`); the text is
 * `errorMessage` or the first validation error `message`. Standalone usage keeps the manual `invalid` input
 * (shown immediately). Stores/emits HTML content instead of plain text, since
 * a native `<textarea>` cannot host tiptap's `contenteditable` ProseMirror view. Visual variants:
 * `variant="field"` (bordered surface, form-field tokens) and `variant="inline"` (no border,
 * no background — for in-place edits). Both support a translated `placeholder` on an empty document.
 * Field labels are owned by the parent (e.g. surrounding form layout), not this control.
 *
 * Behavioral invariants:
 * - Full tiptap feature set is enabled: headings, marks (bold/italic/underline/strike/sub/superscript/highlight/
 *   color), text alignment, bullet/ordered/task lists, tables (with column/row bubble menus), resizable images,
 *   links, and YouTube embeds. Formatting controls are shown as bubble/floating menus on selection (`showMenu`),
 *   not a fixed toolbar.
 * - `maxLength` counts the editor's plain-text length (`editor.getText().length`), not raw HTML length, and
 *   renders the same `n / max` counter markup/classes as `proffeo-ui-textarea`.
 * - `readonly` and `disabled` both call `editor.setEditable(false)`.
 * - `autofocus` focuses the editor and places the caret at the end once the ProseMirror view is mounted.
 *
 * @selector proffeo-ui-tiptap-editor
 */
@Component({
  selector: 'proffeo-ui-tiptap-editor',
  imports: [
    TiptapEditorDirective,
    TranslatePipe,
    MatIconModule,
    EditorTextMenuComponent,
    EditorCustomNodeMenuComponent,
    EditorNewBlockMenuComponent,
    TableColumnMenuComponent,
    TableRowMenuComponent
  ],
  templateUrl: './ui-tiptap-editor.component.html',
  styleUrl: './ui-tiptap-editor.component.css',
  host: {
    class: 'block min-w-0',
    '[class.proffeo-ui-tiptap-editor--field]': 'variant() === "field"',
    '[class.proffeo-ui-tiptap-editor--inline]': 'variant() === "inline"',
    '[class.proffeo-ui-tiptap-editor--invalid]': 'showError()',
    '[class.proffeo-ui-tiptap-editor--disabled]': 'disabled()',
    '[class.proffeo-ui-tiptap-editor--readonly]': 'readonly()'
  }
})
export class UiTiptapEditorComponent implements OnInit, OnDestroy, FormValueControl<string | null> {
  private readonly binding = injectUiFormControlBinding();
  private readonly injector: Injector = inject(Injector);
  private readonly translateService: TranslateService = inject(TranslateService);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private readonly langVersion = signal(0);

  private isSyncingFromValue = false;

  protected readonly plainTextLength = signal(0);

  protected readonly translatedPlaceholder = computed((): string => {
    this.langVersion();
    const key = this.placeholder();
    return key ? this.translateService.instant(key) : '';
  });

  protected readonly showCounter = computed((): boolean => this.maxLength() !== undefined);

  protected readonly counterExceeded = computed((): boolean => {
    const max = this.maxLength();
    return max !== undefined && this.plainTextLength() >= max;
  });

  /** Single source of truth for the error state (host class, message). */
  protected readonly showError = computed((): boolean => this.binding.showError(this.invalid(), this.touched()));

  protected readonly errorText = computed((): string => proffeoUiFormErrorMessage(this.errorMessage(), this.errors()));

  protected readonly showErrorMessage = computed((): boolean => this.showError() && this.errorText().length > 0);

  /** @summary Current HTML content. */
  public readonly value = model<string | null>(null);

  /** @summary Disables interaction and applies the disabled surface (bound by `[formField]`). */
  public readonly disabled = input<boolean>(false);

  /** @summary Visual variant: `field` (bordered surface) or `inline` (no border/background). */
  public readonly variant = input<ProffeoUiTiptapEditorVariant>('field');

  /** @summary i18n key for the tiptap placeholder shown on an empty document. */
  public readonly placeholder = input<string>('');

  /** @summary Read-only: value visible and focusable but not editable (bound by `[formField]`). */
  public readonly readonly = input<boolean>(false);

  /**
   * @summary Invalid state — error border color and optional message below. Bound by `[formField]` (shown once
   * touched); standalone `[(value)]` usage sets it manually and the error shows immediately.
   */
  public readonly invalid = input<boolean>(false);

  /** @summary Touched state — bound by `[formField]`; gates the error display of form-bound controls. */
  public readonly touched = input<boolean>(false);

  /** @summary Validation errors — bound by `[formField]`; the first `message` is the fallback error text. */
  public readonly errors = input<ProffeoUiFormErrors>([]);

  /** @summary i18n key rendered below the control while the error is shown (overrides validation messages). */
  public readonly errorMessage = input<string>('');

  /**
   * @summary Maximum plain-text length; also renders a `n / max` counter below the control
   * (bound by `[formField]` from the `maxLength()` rule).
   */
  public readonly maxLength = input<number | undefined>(undefined);

  /** @summary Marks the control as required (bound by `[formField]`; surfaced for consumers' own validation). */
  public readonly required = input<boolean>(false);

  /** @summary Focuses the editor and places the caret at the end of the content once it mounts. */
  public readonly autofocus = input<boolean>(false);

  /** @summary Shows the bubble/floating formatting menus (bold, headings, tables, …). */
  public readonly showMenu = input<boolean>(true);

  /** @summary CSS `min-height` of the editable surface. */
  public readonly minHeight = input<string>('unset');

  /** @summary CSS `max-height` of the editable surface (content scrolls beyond it). */
  public readonly maxHeight = input<string>('unset');

  /** @summary Emits when the editor loses focus — marks a `[formField]` as touched. */
  public readonly touch = output<void>();

  public readonly editor: Editor = new Editor({
    extensions: [
      StarterKit.configure({
        bulletList: {
          keepMarks: true,
          keepAttributes: false
        },
        orderedList: {
          keepMarks: true,
          keepAttributes: false
        }
      }),
      Placeholder.configure({
        placeholder: () => this.translatedPlaceholder()
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph']
      }),
      TextStyle,
      Highlight,
      Superscript,
      Subscript,
      Typography,
      Color,
      Table,
      TableHeader,
      TableCell,
      TableRow,
      TaskItem,
      TaskList,
      ResizableImageExtension(this.injector),
      Youtube
    ]
  });

  public constructor() {
    this.translateService.onLangChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.langVersion.update(version => version + 1);
    });

    effect(() => {
      const nextValue = this.value() ?? '';
      if (this.editor.getHTML() === nextValue) {
        return;
      }

      this.isSyncingFromValue = true;
      this.editor.commands.setContent(nextValue, { emitUpdate: false });
      this.isSyncingFromValue = false;
      this.plainTextLength.set(this.editor.getText().length);
    });

    effect(() => {
      // Changing interaction mode is not a content edit (TipTap emits update by default).
      this.editor.setEditable(!this.disabled() && !this.readonly(), false);
    });

    effect(() => {
      this.translatedPlaceholder();
      this.refreshPlaceholderDecorations();
    });
  }

  public ngOnInit(): void {
    this.editor.on('update', () => {
      if (this.isSyncingFromValue) {
        return;
      }

      const html = this.editor.getHTML();
      this.plainTextLength.set(this.editor.getText().length);
      this.value.set(html);
    });

    this.editor.on('blur', () => {
      this.touch.emit();
    });

    if (this.autofocus()) {
      this.editor.commands.focus('end');
    }
  }

  public ngOnDestroy(): void {
    this.editor.destroy();
  }

  /** @summary Focuses the editor (used by Signal Forms `focusBoundControl()`). */
  public focus(options?: FocusOptions): void {
    if (this.editor.isDestroyed || !this.editor.view) {
      return;
    }

    this.editor.commands.focus(null, { scrollIntoView: !options?.preventScroll });
  }

  private refreshPlaceholderDecorations(): void {
    if (this.editor.isDestroyed || !this.editor.view) {
      return;
    }

    this.editor.view.dispatch(this.editor.state.tr);
  }
}
